import {
  Box,
  TableRow,
  TableCell,
  IconButton,
} from "@mui/material";
import { FunctionComponent, useContext } from "react";
import { DeliveryProgressType, InquiryType } from "types/InquiryTypes";
// EE-238: util/formatter and util/DateUtil both export a `reformatDate`, with
// different output, and the two dashboards imported one each. Both now read
// from DateUtil, and the choice of helper follows what the value means rather
// than which file it lives in: a delivery date is a date, a created stamp is a
// moment. Inquiries are routinely created in bulk on one day, and this table
// sorts by created - without the time every row reads as the same instant.
import { reformatDate, reformatDateTime } from "util/DateUtil";
import AppStateContext from "contexts/AppStateContext";
import { apiPreviewInquiryFromId } from "util/network/Inquiries";
import { ReactComponent as MessageIcon } from "../../assets/svg/messageOutsorced.svg";
import StatusToken from "components/Dashboard/StatusToken";
import StatusUpdateIndicator from "components/Dashboard/StatusUpdateIndicator";
import {
  inquiryStatusLabel,
  inquiryDisplayLabel,
  INQUIRY_STATUS_COLORS,
  inquiryTypeLabel,
  GOODS_IN_COLORS,
} from "./constants/inquiryStatusColors";
import { Download } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import Tooltip, { TooltipProps, tooltipClasses } from "@mui/material/Tooltip";
import CopyPhone from "components/Copy/CopyPhone";
import CopyEmail from "components/Copy/CopyEmail";
import { ProductTypeEnum } from "types/products/ProductCommandsType";
import {
  CELL_PRIMARY,
  CELL_SECONDARY,
  ROW_FONT_SECONDARY,
  SP_1,
  SP_2,
  STACKED_CELL,
} from "components/Dashboard/dashboardTable";

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "rgba(0, 0, 0, 0.87)",
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}));

// An unset deadline comes back from the API as the epoch, which reformatDate
// renders as 01/01/1970 - show a dash instead. Mirrors formatDateOrDash in
// RequisitionTable (EE-238).
// One line for the goods-in rollup. What is waiting to be collected leads -
// it is the thing a buyer acts on - then Received as the fraction and Ready
// to use after it (EE-253), the order a piece goes through them.
//
// Ready to pick up is a total of what the supplier announced, not of what is
// left, so it is reported as the gap to Received - "4 to collect" is the
// thing a buyer would act on, where a bare "10 ready" next to "6 received"
// reads as sixteen pieces.
const describeProgress = (progress: DeliveryProgressType): string => {
  // Defaulted, not destructured bare: a count the backend omits would
  // otherwise read as undefined and every sum off it as NaN, which draws a
  // blank bar and says nothing about why.
  const total = progress.total ?? 0;
  const received = progress.received ?? 0;
  const ready = progress.ready ?? 0;
  const readyToUse = progress.readyToUse ?? 0;
  if (received === 0 && ready === 0 && readyToUse === 0)
    return `0 / ${total} received`;
  const toCollect = Math.max(0, ready - received);
  const parts: string[] = [];
  if (toCollect > 0)
    parts.push(
      received > 0
        ? `${toCollect} to collect`
        : `${toCollect} / ${total} ready to pick up`
    );
  // Ready to use never runs ahead of Received, so a non-zero one always
  // follows a Received part here.
  if (received > 0) parts.push(`${received} / ${total} received`);
  if (readyToUse > 0) parts.push(`${readyToUse} ready to use`);
  return parts.join(" \u00b7 ");
};

const DeliveryStatus: FunctionComponent<{
  progress: DeliveryProgressType | null | undefined;
}> = ({ progress }) => {
  if (!progress) return <span style={CELL_PRIMARY}>-</span>;
  const total = progress.total ?? 0;
  const received = progress.received ?? 0;
  const ready = progress.ready ?? 0;
  const readyToUse = progress.readyToUse ?? 0;
  // Everything checked over is what Completed means - arriving is not the
  // finish line any more, so the green reads off ready to use.
  const complete = total > 0 && readyToUse >= total;
  const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "2px",
        // A bar needs a width to mean anything, and the shortest rollup
        // ("0 / 1 received") would leave it a stub that reads as an error.
        minWidth: "96px",
      }}
    >
      <span
        style={{
          ...CELL_PRIMARY,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          ...(complete ? { color: "var(--darkGreen)" } : {}),
        }}
      >
        {describeProgress(progress)}
      </span>
      {/* The same four segments as the dialog's delivery headline
          (DeliveryHeadline in InquiryDialog), only thinner: a row is scanned
          for how far along it is, the dialog is where the numbers are read.
          Counting "4 / 6" across forty rows is what the bar saves. */}
      {total > 0 && (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            height: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{ width: `${pct(readyToUse)}%`, bgcolor: GOODS_IN_COLORS.readyToUse }}
          />
          <Box
            sx={{
              width: `${pct(Math.max(0, received - readyToUse))}%`,
              bgcolor: GOODS_IN_COLORS.received,
            }}
          />
          <Box
            sx={{
              width: `${pct(Math.max(0, ready - received))}%`,
              bgcolor: GOODS_IN_COLORS.readyToPickUp,
            }}
          />
          <Box sx={{ flexGrow: 1, bgcolor: "action.disabled" }} />
        </Box>
      )}
    </div>
  );
};

const formatDeadline = (date: string | null) => {
  if (!date) return "-";
  const formatted = reformatDate(date);
  return formatted === "01/01/1970" ? "-" : formatted;
};

// Cells in the row above, and therefore what the header, the skeleton and the
// expanded row must span. It lived in OutsourcingTable as COLUMN_COUNT = 8
// while the expanded row hard-coded colSpan={9}; one of the two was always
// wrong. It is declared here, next to the cells it counts, and the table
// imports it - it already imports this module.
export const INQUIRY_COLUMN_COUNT = 10;

// Long enough to tell two notes apart at a glance, short enough that the
// column does not push the deadline off screen. Matches the requisition
// board's comment preview.
const NOTE_PREVIEW_CHARS = 40;

// The whole row is a click target that toggles the expander, so anything
// inside it that does its own thing has to stop the event. This used to be
// three separate inline wrappers.
const swallowRowClick = (e: { stopPropagation: () => void }) =>
  e.stopPropagation();

// Inquiries raised from a Requisition get a title of the shape
// "PR-000004 - Raw Material EasyPartz ApS" (RequisitionEnquiryController).
// That is the most repeated value on this board, and as one string it is
// unreadable and unclickable - split it so the number can lead the cell.
// Both prefixes: requisition numbers read REQ-000123 now, but every Inquiry
// title created before that carries PR- baked into the string, and those rows
// are not going to be rewritten.
const REQUISITION_TITLE = /^((?:REQ|PR)-\d{6})\s*-\s*(.*)$/;

const splitRequisitionTitle = (title: string | undefined) => {
  const match = title ? REQUISITION_TITLE.exec(title) : null;
  return match ? { requisitionNo: match[1], rest: match[2] } : null;
};

type PropsType = {
  inquiry: InquiryType;
  refresh: Function;
  // Clicking the row opens the inquiry, the way a requisition row opens its
  // detail dialog. The row no longer edits or changes status itself.
  // Optional: a host that does not open inquiries gets rows that are plainly
  // not clickable, rather than a pointer cursor over a dead click.
  open?: (inquiry: InquiryType) => void;
};

// Inquiry row
const OutsourcingRow: FunctionComponent<PropsType> = ({
  inquiry,
  refresh,
  open,
}) => {
  const { token } = useContext(AppStateContext);

  const handleDownloadPDF = () => {
    apiPreviewInquiryFromId(token, inquiry.id, "pdf")
      .then((response) => response.blob())
      .then((blob) => {
        if (blob instanceof Blob) {
          const url = window.URL.createObjectURL(
            new Blob([blob], { type: "application/pdf" }),
          );

          const newWindow = window.open(url, "_blank");
          if (newWindow) {
            newWindow.onload = () => {
              newWindow.print();
              window.URL.revokeObjectURL(url);
            };

            setTimeout(() => {
              if (!newWindow.closed) {
                newWindow.print();
                window.URL.revokeObjectURL(url);
              }
            }, 1000);
          }

          const a = document.createElement("a");
          a.href = url;
          a.download = "InquiryPreview.pdf";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } else {
          console.error("Expected blob but received:", blob);
        }
      })
      .catch((error) => {
        console.error("Error fetching and downloading PDF:", error);
      });
  };

  const formatId = (id: any) => {
    const strId = id.toString();
    return strId.length === 3 ? `0${strId}` : strId;
  };

  // The customer company, off the order's or quote's author - `customer`,
  // sent on list rows too. Left out, not dashed, when there is none.
  const company = inquiry.customer?.defaultBillingAddress?.company;

  // "3 CNC · 1 Other" rather than four separately-rendered spans, so the
  // caption is one string and can be truncated like any other.
  // Who is being asked, while nobody has been settled on. The backend caps
  // what it sends per row and reports the real total, so the overflow count
  // is the rest of the shortlist rather than the rest of what fits.
  const potentialSuppliers = inquiry.potentialSuppliers ?? [];
  const hiddenSupplierCount = Math.max(
    (inquiry.potentialSupplierCount ?? potentialSuppliers.length) -
      potentialSuppliers.length,
    0
  );

  // Truncated in the row with the full text on hover, the same way the
  // requisition board shows its comments.
  const latestNote = (inquiry.latestNote ?? "").trim();
  const shortenedNote =
    latestNote.length > NOTE_PREVIEW_CHARS
      ? `${latestNote.slice(0, NOTE_PREVIEW_CHARS)}...`
      : latestNote;

  const itemBreakdown = (
    [
      [ProductTypeEnum.CNC, "CNC"],
      [ProductTypeEnum.PRINT3D, "3D"],
      [ProductTypeEnum.SHEET, "Sheet"],
      [ProductTypeEnum.OTHER, "Other"],
    ] as const
  )
    .map(([type, label]) => {
      const count = inquiry.itemTypes?.[type] ?? 0;
      return count > 0 ? `${count} ${label}` : null;
    })
    .filter(Boolean)
    .join(" \u00b7 ");

  // What the inquiry is for, as a primary line plus a caption. Three shapes:
  // raised from a Requisition (the common one since EE-253), attached to an
  // order or quote, or free-standing with just a title.
  const renderForCell = () => {
    const requisition = splitRequisitionTitle(inquiry.title);
    if (requisition) {
      return (
        <div style={STACKED_CELL}>
          <span
            style={{ ...CELL_PRIMARY, fontVariantNumeric: "tabular-nums" }}
          >
            {requisition.requisitionNo}
          </span>
          <span style={CELL_SECONDARY}>{requisition.rest}</span>
        </div>
      );
    }

    if (inquiry.title) {
      return (
        <div style={STACKED_CELL}>
          <span style={CELL_PRIMARY}>{inquiry.title}</span>
          {company && <span style={CELL_SECONDARY}>{company}</span>}
        </div>
      );
    }

    const link = inquiry.orderId
      ? { label: "Order", href: `/ordrestyring/${inquiry.orderId}`, id: inquiry.orderId }
      : inquiry.quoteId
        ? { label: "Quote", href: `/quotes/${inquiry.quoteId}`, id: inquiry.quoteId }
        : null;

    if (!link) {
      return <span style={{ ...CELL_SECONDARY, fontStyle: "italic" }}>No reference</span>;
    }

    return (
      <div style={STACKED_CELL}>
        <span style={CELL_PRIMARY}>
          {link.label}{" "}
          <a
            href={link.href}
            onClick={swallowRowClick}
            style={{
              textDecoration: "underline",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatId(link.id)}
          </a>
        </span>
        {company && <span style={CELL_SECONDARY}>{company}</span>}
      </div>
    );
  };

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: open ? "pointer" : "default" }}
        onClick={open ? () => open(inquiry) : undefined}
      >
        <TableCell align="left" sx={{ paddingLeft: SP_2 }}>
          {/* A token, not a control. Status is changed in the dialog. The
              marker beside it says the requester changed something here and
              nobody has opened it since - same as on the requisition list. */}
          <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <StatusToken
              label={inquiryDisplayLabel(inquiry)}
              colors={INQUIRY_STATUS_COLORS[inquiry.state]}
            />
            <StatusUpdateIndicator
              pending={!!inquiry.statusAckPending}
              statusLabel={inquiryStatusLabel(inquiry.state)}
              changedAt={inquiry.statusChangedAt}
            />
          </div>
        </TableCell>

        {/* Inquiry: the id leads, its type is the caption. */}
        <TableCell align="left" scope="row">
          <div style={{ display: "flex", gap: SP_2, alignItems: "center" }}>
            <LightTooltip
              title={
                <span style={{ fontSize: "0.9rem" }}>
                  {inquiry.message || "No message"}
                </span>
              }
            >
              <span
                style={{
                  display: "flex",
                  // An inquiry with no message still renders the icon, or the
                  // column loses its rhythm - it just reads as absent.
                  opacity: inquiry.message ? 1 : 0.3,
                }}
              >
                <MessageIcon />
              </span>
            </LightTooltip>
            <span style={STACKED_CELL}>
              <span style={{ ...CELL_PRIMARY, fontVariantNumeric: "tabular-nums" }}>
                {inquiry.inquiryNo ?? `#${inquiry.id}`}
              </span>
              <span style={CELL_SECONDARY}>{inquiryTypeLabel(inquiry.type)}</span>
            </span>
          </div>
        </TableCell>

        {/* Requirement: what this inquiry is being raised against. One fact
            per line, and no second copy of created_by - that lives in Raised
            by. */}
        <TableCell align="left">{renderForCell()}</TableCell>

        <TableCell align="left">
          <div style={STACKED_CELL}>
            <span style={{ ...CELL_PRIMARY, fontVariantNumeric: "tabular-nums" }}>
              {inquiry.itemCount}
            </span>
            <span style={CELL_SECONDARY}>{itemBreakdown || "\u2014"}</span>
          </div>
        </TableCell>

        {/* Supplier, after what is being bought - the same order as the
            requisition board. Since EE-253 an inquiry created by a requisition
            submission has no partner until a buyer picks one, so `partner`
            itself can be null - not just `partner.info`. */}
        <TableCell align="left">
          {inquiry.partner ? (
            <div style={STACKED_CELL}>
              <span style={CELL_PRIMARY}>{inquiry.partner.info?.company}</span>
              <span
                style={{
                  ...CELL_SECONDARY,
                  display: "flex",
                  alignItems: "center",
                  gap: SP_1,
                }}
              >
                <Tooltip title="Contact person">
                  <span>
                    {inquiry.partner.info?.firstName}{" "}
                    {inquiry.partner.info?.lastName}
                  </span>
                </Tooltip>
                <span
                  style={{ display: "inline-flex", lineHeight: 0 }}
                  onClick={swallowRowClick}
                >
                  <CopyPhone phone={inquiry.partner.info?.phone} />
                </span>
                <span
                  style={{ display: "inline-flex", lineHeight: 0 }}
                  onClick={swallowRowClick}
                >
                  <CopyEmail
                    email={inquiry.partner.info?.email}
                    subject={`Inquiry: #${inquiry.id}`}
                  />
                </span>
              </span>
            </div>
          ) : (
            // No supplier settled on yet - which is the normal state of a
            // requisition-born inquiry, not a gap. Said in words, since a
            // frame full of names read as "supplier not chosen" to a buyer
            // who had already shortlisted two. The dashed frame below it is
            // the shortlist: who is being asked, up to three, and a settled
            // supplier renders without a frame.
            <div style={STACKED_CELL}>
              <span style={CELL_SECONDARY}>Final supplier not chosen</span>
              {potentialSuppliers.length > 0 && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: `2px ${SP_2}`,
                    borderRadius: "5px",
                    border: "1px dashed var(--black25)",
                    color: "var(--iconGrey)",
                    fontSize: ROW_FONT_SECONDARY,
                  }}
                >
                  {potentialSuppliers
                    .map((candidate) => candidate.partnerName)
                    .join(", ")}
                  {hiddenSupplierCount > 0 && ` +${hiddenSupplierCount} more`}
                </span>
              )}
            </div>
          )}
        </TableCell>

        <TableCell align="left">
          <div style={STACKED_CELL}>
            <span style={CELL_PRIMARY}>{inquiry.created_by}</span>
            <span
              style={{ ...CELL_SECONDARY, fontVariantNumeric: "tabular-nums" }}
            >
              {reformatDateTime(inquiry.created)}
            </span>
          </div>
        </TableCell>

        {/* Deadline: the date alone. The method used to be its caption; it
            now heads the Delivery status column, which is about the same
            thing - how and whether the goods are getting here. */}
        <TableCell align="right">
          <span style={{ ...CELL_PRIMARY, fontVariantNumeric: "tabular-nums" }}>
            {formatDeadline(inquiry.deliveryDate)}
          </span>
        </TableCell>

        {/* Delivery status: the goods-in rollup once the inquiry is in Pickup
            / Delivery ("4 / 6 received", "3 / 6 ready to pick up"), the
            method under it. "-" before that - the backend sends null until
            arrivals are a thing. Green once everything is in: the cue to run
            QC and mark it Ready to use, since nothing moves it there
            automatically. */}
        <TableCell align="right">
          <div style={{ ...STACKED_CELL, alignItems: "flex-end" }}>
            <DeliveryStatus progress={inquiry.deliveryProgress} />
            <span style={CELL_SECONDARY}>{inquiry.deliveryMethod || "\u2014"}</span>
          </div>
        </TableCell>

        {/* Latest note: the buyer's own record of a call or a quoted price.
            It lived only in the history panel, one click deep, which is no
            use when the question is which of forty inquiries needs chasing.
            Notes only - status changes and sends share that trail and are
            not notes. */}
        <TableCell align="left">
          {latestNote ? (
            <Tooltip title={latestNote}>
              <span style={CELL_SECONDARY}>{shortenedNote}</span>
            </Tooltip>
          ) : (
            <span style={CELL_SECONDARY}>-</span>
          )}
        </TableCell>

        <TableCell align="right">
          <span style={{ whiteSpace: "nowrap" }} onClick={swallowRowClick}>
            {/* The only action left in the row: the email preview is the one
                thing worth having without opening anything. */}
            <Tooltip title="Download email preview">
              <IconButton onClick={() => handleDownloadPDF()}>
                <Download />
              </IconButton>
            </Tooltip>
          </span>
        </TableCell>
      </TableRow>
    </>
  );
};

export default OutsourcingRow;
